<script setup lang="ts">
import type { Leg, Stopover } from '@optionway/models';
import { CircleAlert } from 'lucide-vue-next';

const prefix = 'components.results.leg';

const locationLabelsStore = useLocationLabels();
const isLg = useMediaQuery('(min-width: 1024px)');

const properties = defineProps<{
  loading?: boolean;
  stopover?: Stopover;
  duration?: Leg['duration'];
  hasTechnicalStop: boolean;
  hideTooltips?: boolean;
}>();

const showStopoverCodes = computed(
  () => (properties.stopover?.count ?? 0) <= 2 && properties.stopover?.details?.every((d) => d.location?.iataCode),
);
</script>

<template>
  <div class="inline-flex flex-col items-center justify-center gap-1">
    <UiSkeleton
      v-if="loading"
      class="h-6 w-20"
    />
    <UiCustomBadge
      v-else
      :variant="displayTypeToVariant(properties.stopover?.displayType)"
      size="s"
    >
      <CircleAlert
        v-if="properties.stopover?.displayType === 'WARNING'"
        :size="12"
        aria-hidden="true"
      />
      <UiTooltip
        v-if="properties.stopover?.count"
        :disabled="properties.hideTooltips"
      >
        <UiTooltipTrigger as-child>
          <span class="whitespace-nowrap">
            {{ $t(`${prefix}.stopover`, properties.stopover.count) }}
            <template v-if="showStopoverCodes">
              {{ $t(`${prefix}.stopoverAt`) }}
              {{ properties.stopover.details?.map((d) => d.location?.iataCode).join(', ') }}
            </template>
            <span
              v-if="properties.stopover.details?.length"
              class="sr-only"
            >
              <template
                v-for="(detail, i) in properties.stopover.details"
                :key="i"
              >
                {{ locationLabelsStore.airport(detail.location?.iataCode) }}
                ({{ detail.location?.iataCode }}): {{ useDurationFormat(detail.duration) }}.
              </template>
            </span>
          </span>
        </UiTooltipTrigger>
        <UiTooltipContent v-if="isLg">
          <div class="flex flex-col">
            <template
              v-for="(detail, i) in properties.stopover.details"
              :key="i"
            >
              <div
                v-if="i > 0"
                class="my-1 border-t border-gray-200 opacity-50"
              />
              <div>
                <i18n-t
                  scope="global"
                  :keypath="`${prefix}.stopoverDuration`"
                >
                  <template #duration>{{ useDurationFormat(detail.duration) }}</template>
                </i18n-t>
                <template v-if="detail.location?.iataCode">
                  - {{ locationLabelsStore.airport(detail.location.iataCode) }} ({{ detail.location.iataCode }})
                </template>
              </div>
            </template>
          </div>
        </UiTooltipContent>
      </UiTooltip>
      <span
        v-else
        class="whitespace-nowrap"
      >
        {{ $t(`${prefix}.stopover`, 0) }}
      </span>
    </UiCustomBadge>

    <UiSkeleton
      v-if="properties.loading"
      class="h-5 w-12"
    />
    <div
      v-else
      class="flex flex-col items-center"
    >
      <div class="justify-center text-center text-xs font-normal leading-normal text-text-secondary-600">
        {{ useDurationFormat(properties.duration) }}
        <template v-if="properties.hasTechnicalStop"> {{ $t(`${prefix}.technicalStop`) }}</template>
      </div>

      <span
        v-if="!properties.loading && (properties.stopover?.cabinClasses.length ?? 0) > 1"
        class="text-center text-xs font-normal text-text-secondary-600"
      >
        <template v-if="(properties.stopover?.cabinClasses.length ?? 0) > 2">{{
          $t(`${prefix}.multipleCabins`)
        }}</template>
        <template v-else>
          <template
            v-for="(cabinClass, index) in properties.stopover?.cabinClasses"
            :key="cabinClass"
            >{{ index > 0 ? ' + ' : '' }}{{ $t(`components.search.cabinClassSelector.${cabinClass}`) }}</template
          >
        </template>
      </span>
    </div>
  </div>
</template>
